import React from 'react';
import {
  Icon,
  Typeahead,
  Spacer,
  BlockList,
  Tag,
  TextCaption,
  TextTitle,
  Stack,
  ResourceCard,
  sendToast,
} from '@pypestream/design-system';
import { StudioResourceTypeEnum } from '@pypestream/api-services';

import { ConfigPanel } from '../src/config-panel';
import { tableStore } from '../../../stores/table-store';
import { useUploadPythonFiles } from '../../../hooks';
import { ResourceLibItem } from '../../../xstate/prostudio.xstate.types';

import './resource-lib.scss';

export const ResourceLibPanel: React.FC<{
  viewOnly?: boolean;
  resourceLibData?: ResourceLibItem[];
  onClose: () => void;
}> = ({ resourceLibData, onClose, viewOnly }) => {
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [selectedType, setSelectedType] =
    React.useState<StudioResourceTypeEnum | null>(null);
  const [filteredOptions, setFilteredOptions] = React.useState<
    ResourceLibItem[]
  >(resourceLibData || []);
  const [value, setValue] = React.useState<string>('');

  const handleTypeheadInput = (event: CustomEvent) => {
    setFilteredOptions(event.detail.filtered);
    setValue(event.detail.value);
  };

  const [isResourceLibAdding, setIsResourceLibAdding] = React.useState(false);
  const { handleUpload } = useUploadPythonFiles('scriptFile');

  const uploadScripts = async (scriptNames: string[]) => {
    try {
      const filePromises = scriptNames.map(async (name) => {
        const response = await fetch(`/scripts/${name}`);

        const contentType = response.headers.get('content-type');

        if (
          !response.ok ||
          (contentType && contentType.includes('text/html'))
        ) {
          console.error(`Script not found: ${name}`);
          return null;
        }

        const blob = await response.blob();
        return new File([blob], name, { type: 'text/x-python' });
      });

      const files = await Promise.all(filePromises);

      const dataTransfer = new DataTransfer();
      files.forEach((file) => file && dataTransfer.items.add(file));

      const fakeEvent = {
        target: { files: dataTransfer.files },
      } as unknown as React.ChangeEvent<HTMLInputElement>;

      await handleUpload(fakeEvent);
    } catch (e) {
      console.error(e);
    }
  };

  const handleAddResourceLib = async (item: ResourceLibItem) => {
    setIsResourceLibAdding(true);

    if (!item.data) {
      return;
    }

    try {
      const scriptNamesList = item.data
        .map((i) => i.command?.trim())
        .filter(Boolean)
        .map((cmd) => `${cmd}.py`);

      await uploadScripts(scriptNamesList);

      tableStore.addResourceLibRows(item.data);
    } catch (e) {
      console.error(e);

      sendToast({
        type: 'error',
        text: 'Failed to add Resource Library',
        autoClose: 3000,
      });
    } finally {
      setIsResourceLibAdding(false);
    }
  };

  return (
    <ConfigPanel
      title={
        <Stack nowrap gutter="xsmall" alignItems="center">
          <TextTitle size="xsmall">Resource Library</TextTitle>
          <Tag minimal variant="indicator">
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8 8.0001H8.00667M10.357 10.3571C7.23283 13.4813 3.64489 14.9587 2.34315 13.6569C1.0414 12.3552 2.51878 8.76727 5.64298 5.64307C8.76717 2.51888 12.3551 1.04149 13.6569 2.34324C14.9586 3.64499 13.4812 7.23292 10.357 10.3571ZM10.357 5.64295C13.4812 8.76715 14.9586 12.3551 13.6568 13.6568C12.3551 14.9586 8.76715 13.4812 5.64295 10.357C2.51876 7.2328 1.04137 3.64487 2.34312 2.34312C3.64487 1.04137 7.2328 2.51876 10.357 5.64295ZM8.33333 8.0001C8.33333 8.18419 8.1841 8.33343 8 8.33343C7.81591 8.33343 7.66667 8.18419 7.66667 8.0001C7.66667 7.816 7.81591 7.66676 8 7.66676C8.1841 7.66676 8.33333 7.816 8.33333 8.0001Z"
                stroke="#5956E3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Beta
          </Tag>
        </Stack>
      }
      onClose={onClose}
    >
      <div className="c-resource-lib__sticky-bar">
        <Typeahead
          searchKeys={['name']}
          data={resourceLibData as ResourceLibItem[]}
          onInput={handleTypeheadInput}
          value={value}
        />
        <Spacer />
        <Tag
          interactive
          minimal
          size="small"
          className="u-margin-right-xsmall"
          onclick={() =>
            setSelectedType((selected) =>
              selected === StudioResourceTypeEnum.MicroAgent
                ? null
                : StudioResourceTypeEnum.MicroAgent
            )
          }
          variant={
            selectedType === StudioResourceTypeEnum.MicroAgent
              ? 'indicator'
              : 'default'
          }
        >
          <Icon name="microagent" />
          Microagents
        </Tag>
        <Tag
          interactive
          minimal
          size="small"
          variant={
            selectedType === StudioResourceTypeEnum.ActionNode
              ? 'indicator'
              : 'default'
          }
          onclick={() =>
            setSelectedType((selected) =>
              selected === StudioResourceTypeEnum.ActionNode
                ? null
                : StudioResourceTypeEnum.ActionNode
            )
          }
        >
          <Icon name="action-node" />
          Action Nodes
        </Tag>
      </div>
      <Spacer size="xsmall" />
      <BlockList>
        {filteredOptions
          .filter((item) => {
            if (selectedType === null) {
              return true;
            }

            return item.type === selectedType;
          })
          .map((item) => (
            <ResourceCard
              key={item.id}
              interactive
              selected={item.id === selectedId}
              disabled={isResourceLibAdding || viewOnly} // TODO: Add disabled state for view only
              onClick={() => {
                if (viewOnly) {
                  return;
                }

                setSelectedId((state) =>
                  state === item.id ? null : item.id || null
                );
                handleAddResourceLib(item);
                onClose();
              }}
              variant={
                item.type === StudioResourceTypeEnum.MicroAgent
                  ? 'default'
                  : 'neutral'
              }
              label={item.name}
              description={item.description}
            >
              <Icon
                name={
                  item.type === StudioResourceTypeEnum.MicroAgent
                    ? 'microagent'
                    : 'action-node'
                }
                size="small"
                slot="label-prefix"
              />
              {item.nodes && (
                <>
                  <Tag minimal size="small" slot="label-suffix">
                    {item.nodes}
                  </Tag>
                  <TextCaption variant="secondary" slot="label-suffix">
                    Nodes
                  </TextCaption>
                </>
              )}
              <Icon
                name="plus"
                size="small"
                slot="hover-action"
                color="purple"
                variant="filled"
                weight="medium"
              />
            </ResourceCard>
          ))}
      </BlockList>
    </ConfigPanel>
  );
};
